import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';
import { SheetContext } from '../providers/SheetContext';
import Waveform from './tracks/Waveform';
import { fetcher } from '../lib/fetcher';
import { File } from '../models/file';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ToastContext } from '../providers/ToastContext';
import { getIconByMediaType } from '../utils/media';
import { formatDuration, formatSize } from '../utils/currency';
import { useAppBridge } from '@shopify/app-bridge-react';
import { userLoggedInFetch } from '../../pages/_app';
// Import Polaris web component types
import '../../types/polaris-web-components.d';

const SORT_ORDERS = ['newest', 'oldest', 'name', 'size'] as const;
type SortOrder = (typeof SORT_ORDERS)[number];
const SORT_LABELS: Record<SortOrder, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  name: 'Name A–Z',
  size: 'Largest first',
};
const SORT_MENU_ID = 'mls-sort-menu';

type LibraryTab = 'all' | 'audio' | 'application' | 'image';

const TABS: { id: LibraryTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'audio', label: 'Audio' },
  { id: 'application', label: 'Archives' },
  { id: 'image', label: 'Artwork' },
];

const SECTION_TITLES: Record<Exclude<LibraryTab, 'all'>, string> = {
  audio: 'Audio',
  application: 'Archives',
  image: 'Artwork',
};

/** s-* field events surface `.value` on the element; the JSX typing is HTMLElement. */
const readFieldValue = (e: React.FormEvent<HTMLElement>): string => {
  const target: unknown = e.currentTarget;
  if (target && typeof target === 'object' && 'value' in target) {
    const candidate = target.value;
    return typeof candidate === 'string' ? candidate : '';
  }
  return '';
};

const isUploading = (file: File) => file.status === 'pending' || file.status === 'processing';

/**
 * Full-length playback source. The pipeline's `preview` asset is a 30s clip
 * while the waveform maps the whole track, so prefer full-length assets —
 * otherwise progress and seeking can never pass 0:30.
 */
const playbackUrl = (file: File): string | null => {
  const assets = file.storage?.assets;
  return (
    assets?.mp3?.secure_url || assets?.original?.secure_url || assets?.preview?.secure_url || null
  );
};

const thumbUrl = (file: File): string | null => {
  const assets = file.storage?.assets;
  return (
    assets?.thumbnail?.secure_url ||
    assets?.small?.secure_url ||
    assets?.original?.secure_url ||
    null
  );
};

// S3-pipeline assets carry no per-asset duration — it lives in metadata.duration.
const audioDuration = (file: File): number | undefined =>
  file.storage?.assets?.original?.duration ??
  file.storage?.assets?.mp3?.duration ??
  file.metadata?.duration;

const uploadedAtMs = (file: File): number => {
  const t = new Date(file.uploadedAt).getTime();
  return Number.isFinite(t) ? t : 0;
};

interface MediaLibraryProps {
  /**
   * Whether the library is on screen. Gates the poll and stops playback when
   * the host hides it; the standalone page is always active.
   */
  active: boolean;
  /** Host-owned upload entry point — the sheet closes itself first, the page just opens the modal. */
  onRequestUpload: () => void;
}

/**
 * The media library itself: search, sort, type tabs, rows/artwork grid, delete,
 * inline audio preview and the upload status bar. Chrome (overlay, dialog,
 * header, upload modal) belongs to the host — see Sheet.tsx and pages/media.
 */
export const MediaLibrary: React.FC<MediaLibraryProps> = ({ active, onRequestUpload }) => {
  const { sheet, clearUploads } = useContext(SheetContext);
  const { show: showToast } = useContext(ToastContext);
  const app = useAppBridge();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOrder>('newest');
  const [tab, setTab] = useState<LibraryTab>('all');
  // Thumbnails whose <img> failed to load; these render the icon placeholder instead.
  const [brokenThumbs, setBrokenThumbs] = useState<Set<string>>(() => new Set());

  const { data: files, mutate } = useSWR<File[]>(
    `${process.env.APP_URL}/api/files?limit=500`,
    (input) => fetcher(app, input),
    {
      // Poll while the library is on screen so uploads surface processing→ready
      // without a manual refresh. refreshWhenHidden defeats SWR's visibility
      // gate, which otherwise pauses the interval inside the Shopify admin
      // iframe. Focus revalidation is off globally (stops the dashboard flash) —
      // re-enable it just for this list while the library is active.
      refreshInterval: active ? 3000 : 0,
      refreshWhenHidden: true,
      revalidateOnFocus: active,
    },
  );

  useEffect(() => {
    if (files) {
      clearUploads();
    }
  }, [files]);

  // ── Inline audio preview ──
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [playhead, setPlayhead] = useState(0);
  // Duration reported by the <audio> element — the authoritative timeline for
  // progress/seek (stored durations are rounded and can drift from the source).
  const [playingDuration, setPlayingDuration] = useState<number | null>(null);

  const stopPreview = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setPlayingId(null);
    setPlayhead(0);
    setPlayingDuration(null);
  }, []);

  const togglePreview = useCallback(
    (file: File) => {
      if (playingId === file._id) {
        stopPreview();
        return;
      }
      const url = playbackUrl(file);
      if (!url) return;
      let audio = audioRef.current;
      if (!audio) {
        audio = new Audio();
        const created = audio;
        created.addEventListener('timeupdate', () => setPlayhead(created.currentTime));
        created.addEventListener('loadedmetadata', () =>
          setPlayingDuration(
            Number.isFinite(created.duration) && created.duration > 0 ? created.duration : null,
          ),
        );
        created.addEventListener('ended', () => {
          setPlayingId(null);
          setPlayhead(0);
        });
        audioRef.current = created;
      }
      audio.src = url;
      setPlayhead(0);
      setPlayingDuration(null);
      setPlayingId(file._id);
      // jsdom (and older WebViews) return undefined from play() instead of a Promise.
      const playback: unknown = audio.play();
      if (playback instanceof Promise) playback.catch(() => stopPreview());
    },
    [playingId, stopPreview],
  );

  // Stop playback when the library goes off screen and on unmount.
  useEffect(() => {
    if (!active) stopPreview();
  }, [active, stopPreview]);
  useEffect(() => stopPreview, [stopPreview]);

  const removeFile = async (file: File) => {
    if (!files) return;
    if (playingId === file._id) stopPreview();

    await mutate(
      files.map((curr) => (curr._id === file._id ? { ...curr, status: 'deleting' } : curr)),
      false,
    );

    const result = await userLoggedInFetch(app)(`${process.env.APP_URL}/api/files/${file._id}`, {
      method: 'DELETE',
    });

    if (result.status !== 200) {
      await mutate();
      // The API refuses to delete a file a release still uses (409) and explains
      // which one — surface that instead of a generic failure.
      const body = await result.json().catch(() => null);
      return showToast({ message: body?.message || 'Failed to delete file', isError: true });
    }
    await mutate(files.filter((currFile) => currFile._id !== file._id));
  };

  // ── Derived views ──
  const library = useMemo(() => (files || []).filter((f) => !isUploading(f)), [files]);

  const uploading = useMemo(
    () => [
      ...(files || []).filter(isUploading),
      // SheetContext deliberately types newUploads as unknown[]; every producer pushes File rows.
      ...sheet.newUploads.filter((item): item is File => !!item && typeof item === 'object'),
    ],
    [files, sheet.newUploads],
  );

  // ── Arrival animation ──
  // Files that just finished processing (uploading → library) slide in and
  // settle with a bubble pop. Layout effect so the class is on the row before
  // its first paint — no flash of the resting state.
  const prevLibraryIdsRef = useRef<Set<string> | null>(null);
  const [arrivals, setArrivals] = useState<Set<string>>(() => new Set());

  useLayoutEffect(() => {
    if (!files) return;
    const ids = new Set<string>();
    library.forEach((f) => {
      if (f._id) ids.add(f._id);
    });
    const prev = prevLibraryIdsRef.current;
    prevLibraryIdsRef.current = ids;
    if (!prev) return; // first snapshot — pre-existing files don't "arrive"
    const fresh: string[] = [];
    ids.forEach((id) => {
      if (!prev.has(id)) fresh.push(id);
    });
    if (fresh.length === 0) return;
    setArrivals((cur) => {
      const next = new Set(cur);
      fresh.forEach((id) => next.add(id));
      return next;
    });
    // Drop the class once the animation has run so later re-renders (poll
    // ticks, sort/search) don't replay it. Detached on purpose: the 3s poll
    // re-runs this effect and a cleanup would cancel the pending removal.
    setTimeout(() => {
      setArrivals((cur) => {
        const next = new Set(cur);
        fresh.forEach((id) => next.delete(id));
        return next;
      });
    }, 700);
  }, [files, library]);

  const counts = useMemo<Record<LibraryTab, number>>(
    () => ({
      all: library.length,
      audio: library.filter((f) => f.type === 'audio').length,
      application: library.filter((f) => f.type === 'application').length,
      image: library.filter((f) => f.type === 'image').length,
    }),
    [library],
  );

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const comparators: Record<SortOrder, (a: File, b: File) => number> = {
      newest: (a, b) => uploadedAtMs(b) - uploadedAtMs(a),
      oldest: (a, b) => uploadedAtMs(a) - uploadedAtMs(b),
      name: (a, b) => (a.metadata?.name || '').localeCompare(b.metadata?.name || ''),
      size: (a, b) => (b.metadata?.size || 0) - (a.metadata?.size || 0),
    };
    const filtered = library
      .filter((f) => !q || (f.metadata?.name || '').toLowerCase().includes(q))
      .sort(comparators[sort]);
    return {
      audio: filtered.filter((f) => f.type === 'audio'),
      application: filtered.filter((f) => f.type === 'application'),
      image: filtered.filter((f) => f.type === 'image'),
    };
  }, [library, search, sort]);

  const showSection = (id: Exclude<LibraryTab, 'all'>) => tab === 'all' || tab === id;
  const visibleCount = (['audio', 'application', 'image'] as const)
    .filter(showSection)
    .reduce((sum, id) => sum + sections[id].length, 0);

  // ── Renderers ──
  const downloadFile = (file: File) => {
    const url = file.storage?.assets?.original?.secure_url;
    if (!url) return;
    // The signed URL is self-authenticating and the file route presigns S3 with
    // an attachment disposition, so a plain anchor click downloads the original
    // without a page navigation or a bearer token.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.metadata?.name || '';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const rowActions = (file: File) => {
    if (file.status === 'deleting') {
      return <s-spinner size='base' accessibilityLabel='Deleting file' />;
    }
    const downloadUrl = file.storage?.assets?.original?.secure_url;
    return (
      <>
        {downloadUrl && (
          <s-button
            variant='tertiary'
            icon='arrow-down'
            accessibilityLabel={`Download ${file.metadata?.name || 'file'}`}
            onClick={() => downloadFile(file)}
          />
        )}
        <s-button
          variant='tertiary'
          icon='x'
          accessibilityLabel={`Remove ${file.metadata?.name || 'file'}`}
          onClick={() => removeFile(file)}
        />
      </>
    );
  };

  const seekOrToggle = useCallback(
    (file: File, e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const fraction =
        rect.width > 0 ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
      const audio = audioRef.current;

      // Scrub when this track is already playing.
      if (playingId === file._id && audio) {
        const dur =
          Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : audioDuration(file);
        if (dur) {
          audio.currentTime = fraction * dur;
          setPlayhead(fraction * dur);
        }
        return;
      }

      // Otherwise start this track at the clicked position once metadata is in.
      if (!playbackUrl(file)) return;
      togglePreview(file);
      const started = audioRef.current;
      if (!started || fraction === 0) return;
      const applySeek = () => {
        if (Number.isFinite(started.duration) && started.duration > 0) {
          started.currentTime = fraction * started.duration;
          setPlayhead(fraction * started.duration);
        }
      };
      if (started.readyState >= 1) applySeek();
      else started.addEventListener('loadedmetadata', applySeek, { once: true });
    },
    [playingId, togglePreview],
  );

  const audioRow = (file: File) => {
    const playing = playingId === file._id;
    const url = playbackUrl(file);
    const wave = file.storage?.assets?.waveform?.secure_url || null;
    const durationSec = playing ? playingDuration ?? audioDuration(file) : audioDuration(file);
    const duration = formatDuration(durationSec);
    const size = formatSize(file.metadata?.size);
    // Wave-less rows (GCS-era files) keep the single meta line.
    const meta = playing
      ? `${formatDuration(playhead) || '0:00'}${duration ? ` / ${duration}` : ''}`
      : [size, duration].filter(Boolean).join(' · ');
    let title = file.metadata?.name || '?';
    if (file.status === 'aborted') title = `${title} (cancelled)`;
    const progress = playing && durationSec ? playhead / durationSec : 0;

    return (
      <div
        className={`mls-row mls-row--audio${playing ? ' is-playing' : ''}${
          arrivals.has(file._id) ? ' mls-arrive' : ''
        }`}
        key={file._id}
      >
        {url ? (
          <button
            type='button'
            className='mls-play'
            aria-label={playing ? `Pause ${title}` : `Play ${title}`}
            onClick={() => togglePreview(file)}
          >
            <FontAwesomeIcon icon={playing ? faPause : faPlay} size='sm' />
          </button>
        ) : (
          <div className='mls-icon mls-icon--audio'>
            <FontAwesomeIcon icon={getIconByMediaType(file.metadata?.type)} />
          </div>
        )}
        <div className='mls-row__body'>
          <div className='mls-row__head'>
            <div className='mls-row__name' title={title}>
              {title}
            </div>
            {wave && size && <span className='mls-row__meta'>{size}</span>}
            <div className='mls-row__actions'>{rowActions(file)}</div>
          </div>
          {wave ? (
            <div className='mls-row__waveline'>
              <span className={`mls-time${playing ? ' is-live' : ''}`}>
                {playing ? formatDuration(playhead) || '0:00' : '0:00'}
              </span>
              <div
                className='mls-wave'
                role='slider'
                tabIndex={0}
                aria-label={`Seek ${title}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                onClick={(e) => seekOrToggle(file, e)}
              >
                <Waveform
                  src={wave}
                  progress={progress}
                  height={26}
                  playedColor='#6366f1'
                  unplayedColor='#ced4e9'
                />
              </div>
              <span className='mls-time'>{duration || '--:--'}</span>
            </div>
          ) : (
            meta && <div className='mls-row__meta'>{meta}</div>
          )}
        </div>
      </div>
    );
  };

  const fileRow = (file: File) => {
    const size = formatSize(file.metadata?.size);
    let title = file.metadata?.name || '?';
    if (file.status === 'aborted') title = `${title} (cancelled)`;

    return (
      <div className={`mls-row${arrivals.has(file._id) ? ' mls-arrive' : ''}`} key={file._id}>
        <div className='mls-icon mls-icon--archive'>
          <FontAwesomeIcon icon={getIconByMediaType(file.metadata?.type)} />
        </div>
        <div className='mls-row__body'>
          <div className='mls-row__name' title={title}>
            {title}
          </div>
          {size && <div className='mls-row__meta'>{size}</div>}
        </div>
        <div className='mls-row__actions'>{rowActions(file)}</div>
      </div>
    );
  };

  const artCard = (file: File) => {
    const thumb = thumbUrl(file);
    const name = file.metadata?.name || '?';
    return (
      <figure className={`mls-art${arrivals.has(file._id) ? ' mls-arrive' : ''}`} key={file._id}>
        {thumb && !brokenThumbs.has(file._id) ? (
          <img
            className='mls-art__img'
            src={thumb}
            alt={name}
            // Guard on _id: adding `undefined` to the set would match every
            // file and collapse the whole grid to icon placeholders.
            onError={() => file._id && setBrokenThumbs((prev) => new Set(prev).add(file._id))}
          />
        ) : (
          <div className='mls-art__empty'>
            <FontAwesomeIcon icon={getIconByMediaType(file.metadata?.type)} />
          </div>
        )}
        <figcaption className='mls-art__name'>{name}</figcaption>
        <div className='mls-art__action'>{rowActions(file)}</div>
      </figure>
    );
  };

  const sectionMarkup = (id: Exclude<LibraryTab, 'all'>) => {
    if (!showSection(id) || sections[id].length === 0) return null;
    return (
      <section key={id}>
        {tab === 'all' && <div className='mls-label'>{SECTION_TITLES[id]}</div>}
        {id === 'image' ? (
          <div className='mls-grid'>{sections.image.map(artCard)}</div>
        ) : (
          sections[id].map(id === 'audio' ? audioRow : fileRow)
        )}
      </section>
    );
  };

  const uploadingNames = uploading
    .map((file) => file.metadata?.name)
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');

  return (
    <div className='mls-library'>
      {/* Search + sort */}
      <div className='mls-toolbar'>
        <div className='mls-toolbar__search'>
          <s-search-field
            label='Search files'
            placeholder='Search files'
            labelAccessibilityVisibility='exclusive'
            value={search}
            onInput={(e: React.FormEvent<HTMLElement>) => setSearch(readFieldValue(e))}
          />
        </div>
        <div className='mls-sort'>
          <s-button
            variant='tertiary'
            icon='sort'
            commandFor={SORT_MENU_ID}
            accessibilityLabel='Sort files'
          >
            {SORT_LABELS[sort]}
          </s-button>
          <s-menu id={SORT_MENU_ID} accessibilityLabel='Sort files'>
            {SORT_ORDERS.map((order) => (
              <s-button
                key={order}
                variant='tertiary'
                icon={sort === order ? 'check' : undefined}
                onClick={() => setSort(order)}
              >
                {SORT_LABELS[order]}
              </s-button>
            ))}
          </s-menu>
        </div>
      </div>

      {/* Type tabs */}
      <div className='rl-tabs' role='tablist'>
        {TABS.map((t) => (
          <button
            key={t.id}
            type='button'
            role='tab'
            aria-selected={tab === t.id}
            className={`rl-tab ${tab === t.id ? 'is-selected' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label} <span className='rl-count'>{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {/* Scrollable content */}
      <div className='mls-body'>
        {visibleCount === 0 ? (
          <div className='mls-empty'>
            <s-text color='subdued'>
              {search.trim() ? 'No files match your search.' : 'No files yet — upload your first.'}
            </s-text>
            {/* The empty library is otherwise a dead end: the upload entry point
                lives in the host's header, which may be off screen (sheet) or
                far from the message (page). */}
            {!search.trim() && (
              <div className='mls-empty__action'>
                <s-button variant='primary' icon='plus' onClick={onRequestUpload}>
                  Upload
                </s-button>
              </div>
            )}
          </div>
        ) : (
          <>
            {sectionMarkup('image')}
            {sectionMarkup('audio')}
            {sectionMarkup('application')}
          </>
        )}
      </div>

      {/* Sticky upload status */}
      {uploading.length > 0 && (
        <div className='mls-uploadbar'>
          <s-spinner size='base' accessibilityLabel='Uploading' />
          <div className='mls-uploadbar__body'>
            <div className='mls-uploadbar__title'>
              Processing {uploading.length} file{uploading.length === 1 ? '' : 's'}…
            </div>
            {uploadingNames && <div className='mls-uploadbar__sub'>{uploadingNames}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
